import React from "react";
import styled from "@emotion/styled";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { toCurrency, toPercent } from "@helpers";
import { GainLossTypography, PercentIndicator } from "@rentablo/components";

const Wrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  width: 70%;
  ${({ theme }) => theme.breakpoints.down("md")} {
    width: 100%;
    margin-bottom: ${({ theme }) => theme.spacing(2)};
  }
`;

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  width: 100%;

  ${({ theme }) => theme.breakpoints.down("md")} {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: ${({ theme }) => theme.spacing(3)};
    justify-content: initial;
  }
`;

const Caption = styled(Typography)`
  font-size: 12px;
  line-height: 1.38;
  color: ${({ theme }) => theme.palette.text.secondary};
  text-transform: uppercase;
`;

const NoBoldTypography = styled(Typography)`
  font-weight: normal;
`;

const Item = styled.div``;

export interface Props {
  showPerformanceReview: boolean;
  gainAndLoss: number;
  returnOnInvestment: number;
  returnOnInvestmentInPercent: number;
  realizedGains: number;
  performance: number;
  currency: string;
}

export const PerformanceOverview = ({
  showPerformanceReview,
  gainAndLoss,
  returnOnInvestment,
  returnOnInvestmentInPercent,
  realizedGains,
  performance,
  currency,
}: Props) => {
  const { t } = useTranslation();

  if (!showPerformanceReview) {
    return null;
  }

  return (
    <Wrapper>
      <Typography variant="body2" mb={6}>
        {t("investment.performance")}
      </Typography>
      <Container>
        <Box>
          <Caption variant="body2">
            {t("performanceOverview.gainAndLoss")}
          </Caption>
          <GainLossTypography variant="subtitle2" value={gainAndLoss}>
            {toCurrency(gainAndLoss, currency)}
          </GainLossTypography>
        </Box>
        <Box>
          <Caption variant="body2">
            {t("performanceOverview.realizedGains")}
          </Caption>
          <NoBoldTypography variant="subtitle2">
            {toCurrency(realizedGains, currency)}
          </NoBoldTypography>
        </Box>
        <Box>
          <Caption variant="body2">
            {t("performanceOverview.returnOnInvestment")}
          </Caption>
          <GainLossTypography
            variant="subtitle2"
            value={returnOnInvestmentInPercent}
            showIndicator={false}
          >
            {toCurrency(returnOnInvestment, currency)} (
            {toPercent(returnOnInvestmentInPercent)})
          </GainLossTypography>
        </Box>
        <Box>
          <Caption variant="body2">
            {t("performanceOverview.performance")}
          </Caption>
          <PercentIndicator
            variant="subtitle2"
            iconSize="12px"
            percent={performance}
          />
        </Box>
      </Container>
    </Wrapper>
  );
};
